import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-all duration-fast disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:opacity-90 shadow-float-1 hover:shadow-float-2",
        destructive: "bg-destructive text-destructive-foreground hover:opacity-90 shadow-float-1 hover:shadow-float-2",
        outline: "border border-foreground/10 bg-background hover:bg-muted/30 hover:border-foreground/20 transition-all duration-fast",
        secondary: "bg-secondary text-secondary-foreground hover:opacity-90 shadow-float-1 hover:shadow-float-2",
        ghost: "hover:bg-muted/50 transition-colors duration-fast",
        link: "text-primary underline-offset-4 hover:underline",
        minimal: "bg-primary text-primary-foreground hover:shadow-glow transition-all duration-fast hover:scale-[1.02] shadow-float-1 rounded-lg border-0 font-light tracking-normal",
      },
      size: {
        default: "h-10 px-6 py-2 text-sm",
        sm: "h-9 px-4 text-sm",
        lg: "h-14 px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
